/**
 * React wrapper for Bryntum Grid
 */
import React from 'react';
import { AlignSpec, Base, CellCopyPaste, CellCopyPasteConfig, CellEdit, CellEditConfig, CellMenu, CellMenuConfig, CellTooltip, CellTooltipConfig, ColumnAutoWidth, ColumnAutoWidthConfig, ColumnDragToolbar, ColumnDragToolbarConfig, ColumnPicker, ColumnPickerConfig, ColumnRename, ColumnRenameConfig, ColumnReorder, ColumnReorderConfig, ColumnResize, ColumnResizeConfig, ColumnStore, ColumnStoreConfig, ContainerItemConfig, ContainerLayoutConfig, FileDrop, FileDropConfig, FillHandle, FillHandleConfig, Filter, FilterBar, FilterBarConfig, FilterConfig, GridColumnConfig, GridExcelExporter, GridExcelExporterConfig, GridFeaturesConfigType, GridGroupSummary, GridGroupSummaryConfig, GridMultiPageExporter, GridMultiPageExporterConfig, GridMultiPageVerticalExporter, GridMultiPageVerticalExporterConfig, GridPdfExport, GridPdfExportConfig, GridSinglePageExporter, GridSinglePageExporterConfig, GridSplit, GridSplitConfig, GridSummary, GridSummaryConfig, Group, GroupConfig, HeaderMenu, HeaderMenuConfig, Layout, Location, Mask, MaskConfig, MergeCells, MergeCellsConfig, Model, ModelConfig, PanelCollapserConfig, PanelCollapserOverlayConfig, PanelHeader, QuickFind, QuickFindConfig, Rectangle, RegionResize, RegionResizeConfig, RowCopyPaste, RowCopyPasteConfig, RowExpander, RowExpanderConfig, RowReorder, RowReorderConfig, ScrollManager, ScrollManagerConfig, Scroller, ScrollerConfig, Search, SearchConfig, Sort, SortConfig, StateProvider, StickyCells, StickyCellsConfig, Store, StoreConfig, Stripe, StripeConfig, SubGridConfig, TabConfig, Tool, ToolConfig, ToolbarConfig, TooltipConfig, Tree, TreeConfig, TreeGroup, TreeGroupConfig, Widget } from '@bryntum/scheduler';
import { Grid } from '@bryntum/scheduler';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumGridProps =  {
     // Configs only
    adopt?                         : HTMLElement|string
    align?                         : AlignSpec|string
    allowOver?                     : boolean
    anchor?                        : boolean
    anchorToTarget?                : boolean
    animateRemovingRows?           : boolean
    ariaDescription?               : string
    ariaLabel?                     : string
    autoClose?                     : boolean
    autoHeight?                    : boolean
    autoShow?                      : boolean
    autoUpdateRecord?              : boolean
    bbar?                          : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    bodyCls?                       : string|object
    bubbleEvents?                  : object
    callOnFunctions?               : boolean
    centered?                      : boolean
    closable?                      : boolean
    closeAction?                   : 'hide'|'destroy'
    collapsed?                     : boolean
    collapsible?                   : boolean|Partial<PanelCollapserConfig>|Partial<PanelCollapserOverlayConfig>
    columns?                       : ColumnStore|Partial<GridColumnConfig>[]|Partial<ColumnStoreConfig>
    config?                        : object
    constrainTo?                   : HTMLElement|Widget|Rectangle
    contentElementCls?             : string|object
    contextMenuTriggerEvent?       : 'contextmenu'|'click'|'dblclick'
    defaultBindProperty?           : string
    defaultFocus?                  : Function
    defaultRegion?                 : string
    defaults?                      : object
    destroyStore?                  : boolean
    disableGridRowModelWarning?    : boolean
    dismissDelay?                  : number
    dock?                          : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?                     : boolean|object
    enableSticky?                  : boolean
    enableTextSelection?           : boolean
    features?                      : object
    fillLastColumn?                : boolean
    fixedRowHeight?                : boolean
    floating?                      : boolean
    focusOnToFront?                : boolean
    footer?                        : object|string
    forElement?                    : HTMLElement
    forSelector?                   : string
    fullRowRefresh?                : boolean
    getHtml?                       : Function|string
    getRowHeight?                  : Function
    header?                        : string|boolean|PanelHeader
    hideAnimation?                 : boolean|object
    hideDelay?                     : number
    hideHorizontalScrollbar?       : boolean
    hideOnDelegateChange?          : boolean
    hideWhenEmpty?                 : boolean
    hoverDelay?                    : number
    htmlCls?                       : string|object
    ignoreParentReadOnly?          : boolean
    itemCls?                       : string
    keyMap?                        : Record<string, string>
    lazyItems?                     : Record<string, Partial<ContainerItemConfig>>|Partial<ContainerItemConfig>[]|Widget[]
    listeners?                     : object
    loadingMsg?                    : string
    loadMask?                      : string|Partial<MaskConfig>|null
    loadMaskDefaults?              : Partial<MaskConfig>
    loadMaskError?                 : Partial<MaskConfig>|Mask|boolean
    localeClass?                   : typeof Base
    localizable?                   : boolean
    localizableProperties?         : string[]
    maskDefaults?                  : Partial<MaskConfig>
    masked?                        : boolean|string|Partial<MaskConfig>
    maximizable?                   : boolean
    maximized?                     : boolean
    modal?                         : boolean
    monitorResize?                 : boolean
    mouseOffsetX?                  : number
    mouseOffsetY?                  : number
    namedItems?                    : Record<string, Partial<ContainerItemConfig>>
    owner?                         : Widget
    plugins?                       : Function[]
    positioned?                    : boolean
    preserveFocusOnDatasetChange?  : boolean
    preserveScrollOnDatasetChange? : boolean
    preventTooltipOnTouch?         : boolean
    relayStoreEvents?              : boolean
    resizeToFitIncludesHeader?     : boolean
    responsiveLevels?              : Record<string, number|string>
    ripple?                        : boolean|object
    rootElement?                   : ShadowRoot
    scrollAction?                  : 'hide'|'realign'|null
    scrollerClass?                 : typeof Scroller
    scrollManager?                 : Partial<ScrollManagerConfig>|ScrollManager
    selectionMode?                 : object
    showAnimation?                 : boolean|object
    showDirty?                     : boolean
    showOnClick?                   : boolean
    showOnHover?                   : boolean
    showTooltipWhenDisabled?       : boolean
    stateful?                      : boolean|object|string[]
    statefulEvents?                : object|string[]
    stateId?                       : string
    stateProvider?                 : StateProvider
    strips?                        : Record<string, Partial<ContainerItemConfig>>
    subGridConfigs?                : Record<string, Partial<SubGridConfig>>
    syncMask?                      : string|Partial<MaskConfig>|null
    tab?                           : boolean|Partial<TabConfig>
    tag?                           : string
    tbar?                          : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    textAlign?                     : 'left'|'center'|'right'|'start'|'end'
    textContent?                   : boolean
    trackMouse?                    : boolean
    trapFocus?                     : boolean
    ui?                            : string|object
    weight?                        : number

     // Configs and properties
    alignSelf?           : string
    appendTo?            : HTMLElement|string
    cls?                 : string|object
    columnLines?         : boolean
    content?             : string
    data?                : object[]|Model[]|Partial<ModelConfig>[]
    dataset?             : object
    disabled?            : boolean
    emptyText?           : string
    enableUndoRedoKeys?  : boolean
    extraData?           : string|number|boolean|object
    flex?                : number|string
    height?              : number|string
    hidden?              : boolean
    hideFooters?         : boolean
    hideHeaders?         : boolean
    html?                : string|Function
    id?                  : string
    insertBefore?        : HTMLElement|string
    insertFirst?         : HTMLElement|string
    items?               : Widget[]|Partial<ContainerItemConfig>[]|Record<string, Partial<ContainerItemConfig>>|Record<string, Partial<ContainerItemConfig>|boolean|null>
    layout?              : Layout|string|Partial<ContainerLayoutConfig>
    layoutStyle?         : object
    longPressTime?       : number
    margin?              : number|string
    maxHeight?           : string|number
    maximizeOnMobile?    : number|string
    maxWidth?            : string|number
    minHeight?           : string|number
    minWidth?            : string|number
    readOnly?            : boolean
    record?              : Model
    rowHeight?           : number
    scrollable?          : Scroller|boolean|Partial<ScrollerConfig>
    store?               : Store|Partial<StoreConfig>
    strictRecordMapping? : boolean
    title?               : string
    tools?               : Record<string, Tool|Partial<ToolConfig>>|Record<string, Tool>|Record<string, Partial<ToolConfig>>
    tooltip?             : string|Partial<TooltipConfig>
    transitionDuration?  : number
    width?               : number|string
    x?                   : number
    y?                   : number

     // Properties only
    anchorSize?      : number[]
    isSettingValues? : boolean
    isValid?         : boolean
    selectedCell?    : Location
    selectedCells?   : Location[]
    selectedRecord?  : Model
    selectedRecords? : Model[]|number[]
    selectedRows?    : Model[]|number[]
    state?           : object
    type?            : string
    values?          : Record<string, object>

     // Events
    onBeforeCancelCellEdit?      : Function
    onBeforeCellDelete?          : Function
    onBeforeCellEditStart?       : Function
    onBeforeClose?               : Function
    onBeforeColumnDragStart?     : Function
    onBeforeColumnDropFinalize?  : Function
    onBeforeCopy?                : Function
    onBeforeDestroy?             : Function
    onBeforeFinishCellEdit?      : Function
    onBeforeHide?                : Function
    onBeforePaste?               : Function
    onBeforePdfExport?           : Function
    onBeforeRenderRow?           : Function
    onBeforeRenderRows?          : Function
    onBeforeSelectionChange?     : Function
    onBeforeSetRecord?           : Function
    onBeforeShow?                : Function
    onBeforeToggleGroup?         : Function
    onBeforeToggleNode?          : Function
    onCancelCellEdit?            : Function
    onCatchAll?                  : Function
    onCellClick?                 : Function
    onCellContextMenu?           : Function
    onCellDblClick?              : Function
    onCellMenuBeforeShow?        : Function
    onCellMenuItem?              : Function
    onCellMenuShow?              : Function
    onCellMenuToggleItem?        : Function
    onCellMouseOut?              : Function
    onCellMouseOver?             : Function
    onCollapseNode?              : Function
    onColumnDrag?                : Function
    onColumnDragStart?           : Function
    onColumnDrop?                : Function
    onContextMenuItem?           : Function
    onContextMenuToggleItem?     : Function
    onCopy?                      : Function
    onDataChange?                : Function
    onDestroy?                   : Function
    onDragSelecting?             : Function
    onExpandNode?                : Function
    onFileDrop?                  : Function
    onFinishCellEdit?            : Function
    onFocusIn?                   : Function
    onFocusOut?                  : Function
    onGridRowAbort?              : Function
    onGridRowBeforeDragStart?    : Function
    onGridRowBeforeDropFinalize? : Function
    onGridRowDrag?               : Function
    onGridRowDragStart?          : Function
    onGridRowDrop?               : Function
    onHeaderMenuBeforeShow?      : Function
    onHeaderMenuItem?            : Function
    onHeaderMenuShow?            : Function
    onHeaderMenuToggleItem?      : Function
    onHide?                      : Function
    onMouseOut?                  : Function
    onMouseOver?                 : Function
    onPaint?                     : Function
    onPaste?                     : Function
    onPdfExport?                 : Function
    onPointerOver?               : Function
    onReadOnly?                  : Function
    onRecompose?                 : Function
    onRenderRow?                 : Function
    onRenderRows?                : Function
    onResize?                    : Function
    onResponsive?                : Function
    onScroll?                    : Function
    onSelectionChange?           : Function
    onSelectionModeChange?       : Function
    onShow?                      : Function
    onSplit?                     : Function
    onSplitterCollapseClick?     : Function
    onStartCellEdit?             : Function
    onSubGridCollapse?           : Function
    onSubGridExpand?             : Function
    onToggleGroup?               : Function
    onToggleNode?                : Function
    onToolClick?                 : Function
    onUnsplit?                   : Function

     // Features
    cellCopyPasteFeature?     : object|boolean|string|CellCopyPaste|Partial<CellCopyPasteConfig>
    cellEditFeature?          : object|boolean|string|CellEdit|Partial<CellEditConfig>
    cellMenuFeature?          : object|boolean|string|CellMenu|Partial<CellMenuConfig>
    cellTooltipFeature?       : object|boolean|string|CellTooltip|Partial<CellTooltipConfig>
    columnAutoWidthFeature?   : object|boolean|string|ColumnAutoWidth|Partial<ColumnAutoWidthConfig>
    columnDragToolbarFeature? : object|boolean|string|ColumnDragToolbar|Partial<ColumnDragToolbarConfig>
    columnPickerFeature?      : object|boolean|string|ColumnPicker|Partial<ColumnPickerConfig>
    columnRenameFeature?      : object|boolean|string|ColumnRename|Partial<ColumnRenameConfig>
    columnReorderFeature?     : object|boolean|string|ColumnReorder|Partial<ColumnReorderConfig>
    columnResizeFeature?      : object|boolean|string|ColumnResize|Partial<ColumnResizeConfig>
    excelExporterFeature?     : object|boolean|string|GridExcelExporter|Partial<GridExcelExporterConfig>
    fileDropFeature?          : object|boolean|string|FileDrop|Partial<FileDropConfig>
    fillHandleFeature?        : object|boolean|string|FillHandle|Partial<FillHandleConfig>
    filterFeature?            : object|boolean|string|Filter|Partial<FilterConfig>
    filterBarFeature?         : object|boolean|string|FilterBar|Partial<FilterBarConfig>
    groupFeature?             : object|boolean|string|Group|Partial<GroupConfig>
    groupSummaryFeature?      : object|boolean|string|GridGroupSummary|Partial<GridGroupSummaryConfig>
    headerMenuFeature?        : object|boolean|string|HeaderMenu|Partial<HeaderMenuConfig>
    mergeCellsFeature?        : object|boolean|string|MergeCells|Partial<MergeCellsConfig>
    multipageFeature?         : object|boolean|string|GridMultiPageExporter|Partial<GridMultiPageExporterConfig>
    multipageverticalFeature? : object|boolean|string|GridMultiPageVerticalExporter|Partial<GridMultiPageVerticalExporterConfig>
    pdfExportFeature?         : object|boolean|string|GridPdfExport|Partial<GridPdfExportConfig>
    quickFindFeature?         : object|boolean|string|QuickFind|Partial<QuickFindConfig>
    regionResizeFeature?      : object|boolean|string|RegionResize|Partial<RegionResizeConfig>
    rowCopyPasteFeature?      : object|boolean|string|RowCopyPaste|Partial<RowCopyPasteConfig>
    rowExpanderFeature?       : object|boolean|string|RowExpander|Partial<RowExpanderConfig>
    rowReorderFeature?        : object|boolean|string|RowReorder|Partial<RowReorderConfig>
    searchFeature?            : object|boolean|string|Search|Partial<SearchConfig>
    singlepageFeature?        : object|boolean|string|GridSinglePageExporter|Partial<GridSinglePageExporterConfig>
    sortFeature?              : object|boolean|string|Sort|Partial<SortConfig>
    splitFeature?             : object|boolean|string|GridSplit|Partial<GridSplitConfig>
    stickyCellsFeature?       : object|boolean|string|StickyCells|Partial<StickyCellsConfig>
    stripeFeature?            : object|boolean|string|Stripe|Partial<StripeConfig>
    summaryFeature?           : object|boolean|string|GridSummary|Partial<GridSummaryConfig>
    treeFeature?              : object|boolean|string|Tree|Partial<TreeConfig>
    treeGroupFeature?         : object|boolean|string|TreeGroup|Partial<TreeGroupConfig>

}

export class BryntumGrid extends React.Component<BryntumGridProps> {

    static instanceClass = Grid;

    static instanceName = 'Grid';

    processWidgetContent = processWidgetContent;

    // Component is View class
    static isView = true;

    // Hidden div to host unused portals DOM
    portalsCache? : HTMLElement;

    // CSS class used for individual portal containers
    portalContainerClass = 'b-react-portal-container';

    state = {
        // Holds React portals
        portals : new Map(),

        // Needed to trigger refresh when portals change
        generation : 0
    };

    static featureNames = [
        'cellCopyPasteFeature',
        'cellEditFeature',
        'cellMenuFeature',
        'cellTooltipFeature',
        'columnAutoWidthFeature',
        'columnDragToolbarFeature',
        'columnPickerFeature',
        'columnRenameFeature',
        'columnReorderFeature',
        'columnResizeFeature',
        'excelExporterFeature',
        'fileDropFeature',
        'fillHandleFeature',
        'filterFeature',
        'filterBarFeature',
        'groupFeature',
        'groupSummaryFeature',
        'headerMenuFeature',
        'mergeCellsFeature',
        'multipageFeature',
        'multipageverticalFeature',
        'pdfExportFeature',
        'quickFindFeature',
        'regionResizeFeature',
        'rowCopyPasteFeature',
        'rowExpanderFeature',
        'rowReorderFeature',
        'searchFeature',
        'singlepageFeature',
        'sortFeature',
        'splitFeature',
        'stickyCellsFeature',
        'stripeFeature',
        'summaryFeature',
        'treeFeature',
        'treeGroupFeature'
    ];

    projectStores = false;

    dataStores = {
        'store': 'data'
    };

    static configNames = [
        'adopt',
        'align',
        'allowOver',
        'anchor',
        'anchorToTarget',
        'animateRemovingRows',
        'ariaDescription',
        'ariaLabel',
        'autoClose',
        'autoHeight',
        'autoShow',
        'autoUpdateRecord',
        'bbar',
        'bodyCls',
        'bubbleEvents',
        'callOnFunctions',
        'centered',
        'closable',
        'closeAction',
        'collapsed',
        'collapsible',
        'columns',
        'config',
        'constrainTo',
        'contentElementCls',
        'contextMenuTriggerEvent',
        'defaultBindProperty',
        'defaultFocus',
        'defaultRegion',
        'defaults',
        'destroyStore',
        'disableGridRowModelWarning',
        'dismissDelay',
        'dock',
        'draggable',
        'enableSticky',
        'enableTextSelection',
        'features',
        'fillLastColumn',
        'fixedRowHeight',
        'floating',
        'focusOnToFront',
        'footer',
        'forElement',
        'forSelector',
        'fullRowRefresh',
        'getHtml',
        'getRowHeight',
        'header',
        'hideAnimation',
        'hideDelay',
        'hideHorizontalScrollbar',
        'hideOnDelegateChange',
        'hideWhenEmpty',
        'hoverDelay',
        'htmlCls',
        'ignoreParentReadOnly',
        'itemCls',
        'keyMap',
        'lazyItems',
        'listeners',
        'loadingMsg',
        'loadMask',
        'loadMaskDefaults',
        'loadMaskError',
        'localeClass',
        'localizable',
        'localizableProperties',
        'maskDefaults',
        'masked',
        'maximizable',
        'maximized',
        'modal',
        'monitorResize',
        'mouseOffsetX',
        'mouseOffsetY',
        'namedItems',
        'owner',
        'plugins',
        'positioned',
        'preserveFocusOnDatasetChange',
        'preserveScrollOnDatasetChange',
        'preventTooltipOnTouch',
        'relayStoreEvents',
        'resizeToFitIncludesHeader',
        'responsiveLevels',
        'ripple',
        'rootElement',
        'scrollAction',
        'scrollerClass',
        'scrollManager',
        'selectionMode',
        'showAnimation',
        'showDirty',
        'showOnClick',
        'showOnHover',
        'showTooltipWhenDisabled',
        'stateful',
        'statefulEvents',
        'stateId',
        'stateProvider',
        'strips',
        'subGridConfigs',
        'syncMask',
        'tab',
        'tag',
        'tbar',
        'textAlign',
        'textContent',
        'trackMouse',
        'trapFocus',
        'ui',
        'weight'
    ];

    static propertyConfigNames = [
        'alignSelf',
        'appendTo',
        'cls',
        'columnLines',
        'content',
        'data',
        'dataset',
        'disabled',
        'emptyText',
        'enableUndoRedoKeys',
        'extraData',
        'flex',
        'height',
        'hidden',
        'hideFooters',
        'hideHeaders',
        'html',
        'id',
        'insertBefore',
        'insertFirst',
        'items',
        'layout',
        'layoutStyle',
        'longPressTime',
        'margin',
        'maxHeight',
        'maximizeOnMobile',
        'maxWidth',
        'minHeight',
        'minWidth',
        'onBeforeCancelCellEdit',
        'onBeforeCellDelete',
        'onBeforeCellEditStart',
        'onBeforeClose',
        'onBeforeColumnDragStart',
        'onBeforeColumnDropFinalize',
        'onBeforeCopy',
        'onBeforeDestroy',
        'onBeforeFinishCellEdit',
        'onBeforeHide',
        'onBeforePaste',
        'onBeforePdfExport',
        'onBeforeRenderRow',
        'onBeforeRenderRows',
        'onBeforeSelectionChange',
        'onBeforeSetRecord',
        'onBeforeShow',
        'onBeforeToggleGroup',
        'onBeforeToggleNode',
        'onCancelCellEdit',
        'onCatchAll',
        'onCellClick',
        'onCellContextMenu',
        'onCellDblClick',
        'onCellMenuBeforeShow',
        'onCellMenuItem',
        'onCellMenuShow',
        'onCellMenuToggleItem',
        'onCellMouseOut',
        'onCellMouseOver',
        'onCollapseNode',
        'onColumnDrag',
        'onColumnDragStart',
        'onColumnDrop',
        'onContextMenuItem',
        'onContextMenuToggleItem',
        'onCopy',
        'onDataChange',
        'onDestroy',
        'onDragSelecting',
        'onExpandNode',
        'onFileDrop',
        'onFinishCellEdit',
        'onFocusIn',
        'onFocusOut',
        'onGridRowAbort',
        'onGridRowBeforeDragStart',
        'onGridRowBeforeDropFinalize',
        'onGridRowDrag',
        'onGridRowDragStart',
        'onGridRowDrop',
        'onHeaderMenuBeforeShow',
        'onHeaderMenuItem',
        'onHeaderMenuShow',
        'onHeaderMenuToggleItem',
        'onHide',
        'onMouseOut',
        'onMouseOver',
        'onPaint',
        'onPaste',
        'onPdfExport',
        'onPointerOver',
        'onReadOnly',
        'onRecompose',
        'onRenderRow',
        'onRenderRows',
        'onResize',
        'onResponsive',
        'onScroll',
        'onSelectionChange',
        'onSelectionModeChange',
        'onShow',
        'onSplit',
        'onSplitterCollapseClick',
        'onStartCellEdit',
        'onSubGridCollapse',
        'onSubGridExpand',
        'onToggleGroup',
        'onToggleNode',
        'onToolClick',
        'onUnsplit',
        'readOnly',
        'record',
        'rowHeight',
        'scrollable',
        'store',
        'strictRecordMapping',
        'title',
        'tools',
        'tooltip',
        'transitionDuration',
        'width',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'isSettingValues',
        'isValid',
        'selectedCell',
        'selectedCells',
        'selectedRecord',
        'selectedRecords',
        'selectedRows',
        'state',
        'type',
        'values'
    ];

    // Component instance
    instance!: Grid;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumGridProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-${BryntumGrid.instanceClass.$name.toLowerCase()}-container`;
        return (
            <React.Fragment>
                <div className={className} ref={(element) => (this.element = element!)}>
                    {Array.from(this.state.portals).map((item) => item[1])}
                </div>
                <div ref={el => (this.portalsCache = el!)} className="b-react-portals-cache" style={{ display : 'none' }}></div>
            </React.Fragment>
        );

    }
}
